/**
 * Every call this portal makes. There are five, and four of them are auth.
 *
 * `credentials: 'same-origin'` on all of them: the session is an `httpOnly`
 * cookie this bundle cannot read, which is the point — a script injected into a
 * documentation page cannot exfiltrate a session it has no access to.
 */

import type { Page, Session } from './types.js';

export class PortalError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'PortalError';
    this.status = status;
    this.code = code;
  }
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, { credentials: 'same-origin', ...init }).catch(() => {
    throw new PortalError(0, 'unreachable', 'The documentation server did not answer.');
  });
  const body: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const b = (body ?? {}) as { error?: string; message?: string };
    throw new PortalError(response.status, b.error ?? 'error', b.message ?? `HTTP ${response.status}`);
  }
  return body as T;
}

export const session = (): Promise<Session> => call<Session>('/__session');

export const login = (email: string, password: string): Promise<Session> =>
  call<Session>('/__login', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email, password }),
  }).then((r) => ({ ...r, signed_in: true }));

export const logout = (): Promise<{ ok: boolean }> =>
  call<{ ok: boolean }>('/__logout', { method: 'POST' });

export const choosePassword = (password: string): Promise<{ ok: boolean }> =>
  call<{ ok: boolean }>('/__password', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ password }),
  });

export interface DeveloperPayload {
  reader: { tenant_id: string; display_name: string };
  pages: Page[];
}

/** The gated content. 401 until signed in — the gate is here, not in the router. */
export const fetchDeveloper = (): Promise<DeveloperPayload> =>
  call<DeveloperPayload>('/__developer');
